import { Close } from "@mui/icons-material";
import { Box, useMediaQuery } from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { motion } from "framer-motion";
import { DateTime } from "luxon";
import { useMemo } from "react";
import { sideEnter as newCardAnimation } from "../../../utils/animations";
import { TDateISO } from "../../../utils/time/interface";
import { EMode } from "../../shared/components/features/event-scheduler/enums/event.enums";
import { IconButtonComponent } from "../IconButtonComponent";
import { TimePickerComponent } from "../TimePickerComponent";
import { EArrivalTimeType } from "./Container";

interface IArrivalDateTimeCardProps {
  iso: TDateISO;
  isoEnd?: TDateISO;
  onChange: (iso: TDateISO, isoEnd?: TDateISO) => void;
  onDelete: () => void;
  isDisabled: boolean;
  dateEnabled?: boolean;
  type: EArrivalTimeType;
  index: number;
  mode: EMode;
  handleRepetitiveStartDateChange?: (value: Date | null) => void;
  minuteStep?: number;
}

export const ArrivalDateTimeCard = ({
  iso,
  isoEnd,
  onChange,
  onDelete,
  dateEnabled = true,
  type,
  isDisabled,
  mode,
  handleRepetitiveStartDateChange,
  minuteStep,
}: IArrivalDateTimeCardProps) => {
  const isWindow = type === EArrivalTimeType.WINDOW;
  const isRepetitiveMode = mode === EMode.REPETITIVE;
  const isXs = useMediaQuery("(max-width:600px)");

  const dt = useMemo(() => {
    const parsed = DateTime.fromISO(iso);
    return parsed.isValid ? parsed : DateTime.now();
  }, [iso]);

  const dtEnd = useMemo(() => {
    if (!isoEnd) return dt.plus({ hours: 1 });
    const parsed = DateTime.fromISO(isoEnd);
    return parsed.isValid ? parsed : dt.plus({ hours: 1 });
  }, [isoEnd, dt]);

  const handleDateChange = (newDate: DateTime | null) => {
    if (!newDate) return;

    const next = dt.set({
      year: newDate.year,
      month: newDate.month,
      day: newDate.day,
      second: 0,
      millisecond: 0,
    });

    if (isWindow && isoEnd) {
      const nextEnd = dtEnd.set({
        year: newDate.year,
        month: newDate.month,
        day: newDate.day,
        second: 0,
        millisecond: 0,
      });
      onChange(next.toISO() as TDateISO, nextEnd.toISO() as TDateISO);
    } else {
      onChange(next.toISO() as TDateISO);
    }
  };

  const handleStartTimeChange = (newTime: DateTime) => {
    const next = dt.set({
      hour: newTime.hour,
      minute: newTime.minute,
      second: 0,
      millisecond: 0,
    });

    if (!next.isValid) return;
    onChange(next.toISO() as TDateISO, isoEnd);
  };

  const handleEndTimeChange = (newTime: DateTime) => {
    const next = dtEnd.set({
      hour: newTime.hour,
      minute: newTime.minute,
      second: 0,
      millisecond: 0,
    });

    if (!next.isValid) return;
    onChange(iso, next.toISO() as TDateISO);
  };

  const shouldUseStartPicker = isRepetitiveMode || !dateEnabled;
  const pickerLabel = shouldUseStartPicker ? "Start" : "Date";

  const shouldRenderTopPicker = isWindow && (dateEnabled || isRepetitiveMode);
  const shouldRenderInlinePicker = !isWindow;

  const handleSharedDatePickerChange = (value: DateTime | null) => {
    if (!value) return;

    if (shouldUseStartPicker) {
      handleRepetitiveStartDateChange?.(value.toJSDate());
      return;
    }

    handleDateChange(value);
  };

  return (
    <Box
      position="relative"
      component={motion.div}
      sx={{
        // FIX: Evita que el card sea más ancho que la pantalla en mobile
        width: { xs: "100%", sm: "400px" },
        maxWidth: "100%",
        boxSizing: "border-box",
        p: { xs: 0.5, sm: 0.75 },
      }}
      {...newCardAnimation}
    >
      <Box
        sx={{
          position: "absolute",
          top: { xs: 8, sm: -5 },
          right: { xs: 4, sm: -5 },
          zIndex: 1,
        }}
      >
        <IconButtonComponent
          theme="orange"
          disabled={isDisabled}
          onClick={onDelete}
          key={`delete-button-${iso}`}
          sx={{ height: { xs: 20, sm: 24 }, width: { xs: 20, sm: 24 } }}
          sxIcon={{ fontSize: "small" }}
        >
          <Close sx={{ fontSize: { xs: "0.7rem", sm: "inherit" } }} />
        </IconButtonComponent>
      </Box>

      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: { xs: 1.5, sm: 1.25 },
          p: { xs: 1.5, sm: 2 },
          border: "1px solid var(--fx-surface-border, #E2E8F0)",
          borderRadius: 2,
          bgcolor: "var(--fx-surface-sunken, #F8FAFC)",
          width: "100%",
          minWidth: 0,
          boxSizing: "border-box",
          overflow: "hidden", // Importante para no ensanchar
        }}
      >
        {shouldRenderTopPicker && (
          <DatePicker
            label={pickerLabel}
            value={dt}
            disabled={isDisabled}
            onChange={handleSharedDatePickerChange}
            slotProps={{
              textField: {
                size: "small",
                fullWidth: true,
                sx: {
                  "& .MuiInputBase-root": {
                    fontSize: { xs: "0.85rem", sm: "0.875rem" },
                  },
                },
              },
              popper: { sx: { zIndex: 999999 } },
            }}
          />
        )}

        {isWindow ? (
          <Box
            sx={{
              display: "grid",
              // Apilados en mobile, dos columnas en desktop
              gridTemplateColumns: isXs ? "1fr" : "1fr 1fr",
              gap: { xs: 1.5, sm: 1.5 },
              width: "100%",
            }}
          >
            <Box sx={{ minWidth: 0 }}>
              <TimePickerComponent
                disabled={isDisabled}
                label="From"
                onChange={handleStartTimeChange}
                iconColor="orange"
                modalKey={`time-picker-from-${iso}`}
                initialTime={dt}
                type={type}
                fullWidth
                minuteStep={minuteStep}
              />
            </Box>

            <Box sx={{ minWidth: 0 }}>
              <TimePickerComponent
                label="To"
                disabled={isDisabled}
                onChange={handleEndTimeChange}
                iconColor="orange"
                modalKey={`time-picker-to-${isoEnd || iso}`}
                initialTime={dtEnd}
                type={type}
                fullWidth
                minuteStep={minuteStep}
              />
            </Box>
          </Box>
        ) : (
          <Box
            sx={{
              display: "grid",
              // FIX: En mobile una sola columna para que el DatePicker respete el ancho
              gridTemplateColumns: isXs ? "1fr" : "minmax(140px, 0.8fr) 1.2fr",
              gap: { xs: 1.5, sm: 1.5 },
              width: "100%",
              alignItems: "center",
            }}
          >
            {shouldRenderInlinePicker && (
              <Box sx={{ minWidth: 0 }}>
                <DatePicker
                  label={pickerLabel}
                  value={dt}
                  onChange={handleSharedDatePickerChange}
                  disabled={isDisabled}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      size: "small",
                      sx: {
                        "& .MuiInputBase-root": {
                          fontSize: { xs: "0.85rem", sm: "0.875rem" },
                        },
                      },
                    } as any,
                    popper: { sx: { zIndex: 999999 } },
                  }}
                />
              </Box>
            )}

            <Box sx={{ minWidth: 0 }}>
              <TimePickerComponent
                label="At"
                onChange={handleStartTimeChange}
                iconColor="orange"
                disabled={isDisabled}
                modalKey={`time-picker-${iso}`}
                initialTime={dt}
                type={type}
                fullWidth
                minuteStep={minuteStep}
              />
            </Box>
          </Box>
        )}
      </Box>
    </Box>
  );
};
